import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import SEO from "../components/SEO";
import { ArrowRight, BookOpen, Compass, Sparkles } from "lucide-react";

/**
 * AcademyWelcome — geschützter Willkommensraum nach erfolgreicher Aktivierung.
 * Block 1 (Mastermatrix v1.1): Visuelle Vorbereitung, noch keine Assessment- oder
 * Entitlement-Logik. Zeigt drei nächste Schritte im Sinn der Master-Matrix §7.
 */
const AcademyWelcome = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      nav("/auth/login?redirect=/academy/welcome", { replace: true });
    }
  }, [user, loading, nav]);

  if (loading || !user) {
    return <div className="min-h-screen" data-testid="academy-welcome-loading" />;
  }

  const firstName = user.first_name || user.name || "";

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="academy-welcome-page">
      <SEO
        title="Willkommen im geschützten SAT-Bereich"
        description="Dein persönlicher Startpunkt in der SAT Academy."
        path="/academy/welcome"
        noindex
      />
      <div className="container-sat max-w-4xl mx-auto">
        <div className="overline mb-4">SAT Academy · Willkommen</div>
        <h1 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-[#F7F4ED]">
          Willkommen{firstName ? <>, <span className="italic text-[#C7A45B]">{firstName}</span></> : null}.
        </h1>
        <p className="serif italic text-white/60 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
          Du bist im geschützten Raum angekommen. Nimm dir einen Moment, atme durch — und wähle deinen ersten Schritt.
        </p>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            {
              icon: BookOpen,
              overline: "Erster Schritt",
              title: "Dein Weg im Überblick",
              body: "Erhalte einen ruhigen Überblick über deinen Kursweg, deine Wochenreisen und die nächsten Live-Momente.",
              to: "/academy/dashboard",
              cta: "Zum Dashboard",
              tid: "academy-welcome-step-dashboard",
            },
            {
              icon: Compass,
              overline: "Zweiter Schritt",
              title: "Standort spüren",
              body: "Nimm dir zehn Minuten für den Circle of Life™ — deine erste ehrliche Landkarte des Jetzt.",
              to: "/self-discovery/circle-of-life",
              cta: "Circle of Life ansehen",
              tid: "academy-welcome-step-circle",
            },
            {
              icon: Sparkles,
              overline: "Dritter Schritt",
              title: "Auf ein Gespräch",
              body: "Falls du eine persönliche Klärung möchtest — buche ein Erstgespräch mit einem SAT-Menschen.",
              to: "/erstgespraech",
              cta: "Erstgespräch",
              tid: "academy-welcome-step-call",
            },
          ].map((s) => (
            <div
              key={s.tid}
              data-testid={s.tid}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-7 flex flex-col hover:border-[#C7A45B]/40 transition"
              style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(16,33,55,0.35))" }}
            >
              <div className="w-11 h-11 rounded-full border border-[#C7A45B]/40 flex items-center justify-center text-[#C7A45B]">
                <s.icon size={18} />
              </div>
              <div className="overline mt-5 text-[#C7A45B]/85">{s.overline}</div>
              <h2 className="serif text-2xl text-[#F7F4ED] mt-2 leading-snug">{s.title}</h2>
              <p className="text-white/65 text-sm leading-relaxed mt-3 flex-1">{s.body}</p>
              <Link
                to={s.to}
                className="mt-6 inline-flex items-center gap-2 text-[#C7A45B] hover:text-[#E0C887] text-sm tracking-wider uppercase transition"
              >
                {s.cta} <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 text-xs text-white/40 tracking-widest uppercase text-center">
          Kein Coaching-Ersatz · keine Diagnose · nur begleiteter Raum.
        </div>
      </div>
    </div>
  );
};

export default AcademyWelcome;
